import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import GoogleIcon from "@mui/icons-material/Google";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import RefreshIcon from "@mui/icons-material/Refresh";

const GmailMailboxesPanel = () => {
  const [mailboxes, setMailboxes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [authUrl, setAuthUrl] = useState("");
  const [authMailboxId, setAuthMailboxId] = useState(null);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [generatingAuthUrl, setGeneratingAuthUrl] = useState(false);
  const [removingCredentialsId, setRemovingCredentialsId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [togglingCleanupId, setTogglingCleanupId] = useState(null);
  const selectedGoogleToken = 0;

  const fetchMailboxes = useCallback(async ({ showFullLoading = false } = {}) => {
    if (showFullLoading) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/gmail-mailboxes`
      );
      setMailboxes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error fetching Gmail mailboxes:", error);
      toast.error("Failed to load standalone Gmail accounts");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchMailboxes({ showFullLoading: true });
  }, [fetchMailboxes]);

  const handleRefresh = async () => {
    await fetchMailboxes();
    window.dispatchEvent(new Event("gmail-status-refreshed"));
  };

  const handleCloseAuthDialog = async () => {
    setShowAuthDialog(false);
    setAuthMailboxId(null);
    await fetchMailboxes();
    window.dispatchEvent(new Event("gmail-status-refreshed"));
  };

  const handleCreateMailbox = async () => {
    setCreating(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/gmail-mailboxes`,
        { label: newEmail.trim() || "Standalone mailbox" }
      );
      setMailboxes((prev) => [response.data, ...prev]);
      setCreateDialogOpen(false);
      setNewEmail("");
      toast.success("Standalone Gmail account created");
    } catch (error) {
      toast.error("Failed to create standalone Gmail account");
    } finally {
      setCreating(false);
    }
  };

  const handleGenerateAuthUrl = async (mailboxId) => {
    setGeneratingAuthUrl(true);
    setAuthMailboxId(mailboxId);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/auth/gmail/url/mailbox/${mailboxId}?tokenIndex=${selectedGoogleToken}`
      );
      setAuthUrl(response.data.authUrl);
      setShowAuthDialog(true);
    } catch (error) {
      toast.error("Failed to generate Gmail authorization URL");
    } finally {
      setGeneratingAuthUrl(false);
    }
  };

  const handleToggleCleanup = async (mailbox, enabled) => {
    setTogglingCleanupId(mailbox._id);
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/gmail-mailboxes/${mailbox._id}/gmail-cleanup`,
        { enabled }
      );
      setMailboxes((prev) =>
        prev.map((item) =>
          item._id === mailbox._id
            ? {
                ...item,
                gmail_cleanup_status: {
                  ...item.gmail_cleanup_status,
                  gmail_auto_cleanup: enabled,
                },
              }
            : item
        )
      );
      window.dispatchEvent(new Event("gmail-status-refreshed"));
    } catch (error) {
      toast.error("Failed to update cleanup setting");
    } finally {
      setTogglingCleanupId(null);
    }
  };

  const handleRemoveCredentials = async (mailboxId) => {
    if (
      !window.confirm(
        "Remove Gmail credentials from this standalone account?"
      )
    ) {
      return;
    }

    setRemovingCredentialsId(mailboxId);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/gmail-mailboxes/${mailboxId}/gmail-credentials`
      );
      await fetchMailboxes();
      toast.success("Gmail credentials removed");
      window.dispatchEvent(new Event("gmail-status-refreshed"));
    } catch (error) {
      toast.error("Failed to remove Gmail credentials");
    } finally {
      setRemovingCredentialsId(null);
    }
  };

  const handleDeleteMailbox = async (mailboxId) => {
    if (
      !window.confirm(
        "Delete this standalone Gmail account from Autobidder? This does not delete the Gmail inbox itself."
      )
    ) {
      return;
    }

    setDeletingId(mailboxId);
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/gmail-mailboxes/${mailboxId}`
      );
      setMailboxes((prev) => prev.filter((item) => item._id !== mailboxId));
      toast.success("Standalone Gmail account deleted");
    } catch (error) {
      toast.error("Failed to delete standalone Gmail account");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Box
        display="flex"
        alignItems="flex-start"
        justifyContent="space-between"
        gap={2}
        flexWrap="wrap"
        mb={1}
      >
        <Box sx={{ maxWidth: 720 }}>
          <Typography variant="h5" gutterBottom sx={{ mb: 0.5 }}>
            Standalone Gmail Cleanup
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Keep cleaning inboxes that are no longer tied to a profile — for
            example after changing or deleting a profile email. Profile Gmail
            accounts are managed on the Profiles tab.
          </Typography>
        </Box>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            startIcon={
              refreshing ? <CircularProgress size={18} /> : <RefreshIcon />
            }
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Add account
          </Button>
        </Box>
      </Box>

      <Divider sx={{ my: 2 }} />

      {loading ? (
        <Box display="flex" justifyContent="center" py={3}>
          <CircularProgress size={28} />
        </Box>
      ) : mailboxes.length === 0 ? (
        <Alert severity="info">
          No standalone Gmail accounts yet. Add one to continue cleaning an old
          inbox after a profile email changes or the profile is removed.
        </Alert>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {mailboxes.map((mailbox) => {
            const connected = mailbox.gmail_status?.connected;
            const cleanupEnabled =
              mailbox.gmail_cleanup_status?.gmail_auto_cleanup;
            const displayLabel =
              mailbox.label || mailbox.gmail_email || "Untitled mailbox";

            return (
              <Paper
                key={mailbox._id}
                variant="outlined"
                sx={{ p: 2, borderRadius: 2 }}
              >
                <Box
                  display="flex"
                  alignItems="flex-start"
                  justifyContent="space-between"
                  gap={2}
                  flexWrap="wrap"
                >
                  <Box>
                    <Typography variant="subtitle1" fontWeight={600}>
                      {displayLabel}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {mailbox.gmail_email || "Gmail not connected"}
                    </Typography>
                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                      {connected && (
                        <Chip
                          size="small"
                          icon={<LinkIcon />}
                          label="Connected"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                      {cleanupEnabled && (
                        <Chip
                          size="small"
                          icon={<DeleteSweepIcon />}
                          label="Auto cleanup"
                          color="warning"
                          variant="outlined"
                        />
                      )}
                      {mailbox.gmail_status?.isNearExpiry && (
                        <Chip
                          size="small"
                          label="Token expiring soon"
                          color="error"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </Box>

                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <FormControlLabel
                      control={
                        <Switch
                          checked={!!cleanupEnabled}
                          onChange={(e) =>
                            handleToggleCleanup(mailbox, e.target.checked)
                          }
                          disabled={
                            togglingCleanupId === mailbox._id ||
                            !mailbox.gmail_cleanup_status?.can_enable
                          }
                          color="primary"
                        />
                      }
                      label="Auto cleanup"
                    />

                    {!connected ? (
                      <Button
                        variant="outlined"
                        startIcon={
                          generatingAuthUrl &&
                          authMailboxId === mailbox._id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <GoogleIcon />
                          )
                        }
                        onClick={() => handleGenerateAuthUrl(mailbox._id)}
                        disabled={
                          generatingAuthUrl && authMailboxId === mailbox._id
                        }
                      >
                        Connect Gmail
                      </Button>
                    ) : (
                      <Button
                        variant="outlined"
                        color="warning"
                        startIcon={
                          removingCredentialsId === mailbox._id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <LinkOffIcon />
                          )
                        }
                        onClick={() => handleRemoveCredentials(mailbox._id)}
                        disabled={removingCredentialsId === mailbox._id}
                      >
                        Disconnect
                      </Button>
                    )}

                    <IconButton
                      color="error"
                      onClick={() => handleDeleteMailbox(mailbox._id)}
                      disabled={deletingId === mailbox._id}
                      title="Delete standalone account"
                    >
                      {deletingId === mailbox._id ? (
                        <CircularProgress size={20} />
                      ) : (
                        <DeleteIcon />
                      )}
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add standalone Gmail account</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the email address for this inbox. You will connect the Gmail
            account in the next step.
          </Typography>
          <TextField
            fullWidth
            label="Email"
            placeholder="e.g. old-john@gmail.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            onClick={handleCreateMailbox}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={18} /> : <AddIcon />}
          >
            {creating ? "Creating..." : "Create"}
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showAuthDialog}
        onClose={handleCloseAuthDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={1}>
            <GoogleIcon color="primary" />
            Connect Gmail for cleanup
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Open this authorization URL in the browser profile where the target
            Gmail account is signed in.
          </Typography>
          <TextField
            fullWidth
            value={authUrl}
            margin="normal"
            InputProps={{
              readOnly: true,
              endAdornment: (
                <IconButton
                  onClick={() => {
                    navigator.clipboard.writeText(authUrl);
                    toast.success("URL copied to clipboard!");
                  }}
                  size="small"
                >
                  <ContentCopyIcon />
                </IconButton>
              ),
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            After authorization, close this dialog to refresh the connection
            status.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAuthDialog} disabled={refreshing}>
            {refreshing ? "Refreshing..." : "Close"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default GmailMailboxesPanel;
